import { createRoute, notFound, redirect } from "@tanstack/react-router";
import { ErrorScreen } from "@/lib/Components/Screens/ErrorScreen";
import { fetchQuery, prefetchQuery } from "@/lib/GraphQLCodegen/fetcher";
import { globalSupplierSelectionList } from "@/app/Suppliers/GraphQL/globalSupplierSelectionList";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { metaQuery } from "@/app/Meta/GraphQL/metaQuery";
import { buildShaQueryKey, fetchBuildSha } from "@/app/Meta/utils/useBuildSha";
import { meQueryOptions } from "@/lib/Auth/useUser";
import { useAuthStore } from "@/lib/Auth/authStore";
import { QueryClient } from "@/lib/ReactQuery/QueryClient";
import { AppLayout } from "@/lib/Components/Layout/Layout/Layout";
import { rootRoute } from "@/routes/root-route";
import { resolveOrgScope } from "@/routes/scope";
import { isRecordId } from "@/routes/recordId";
import { rememberLastScope } from "@/routes/lastScope";
import {
  supplierParamToId,
  SUPPLIER_LIST_VARIABLES,
} from "@/app/Suppliers/Utils/globalSupplier";
import { z } from "zod";

const globalSearchParams = z.object({
  vehicleId: z.string().optional(),
  officeId: z.string().optional(),
  relocationPageId: z.string().optional(),
  contactId: z.string().optional(),
  proDriverId: z.string().optional(),
});

export const appLayoutRoute = createRoute({
  errorComponent: ErrorScreen,
  validateSearch: globalSearchParams,
  beforeLoad: async ({ params, location }) => {
    const { tenant, orgId, supplierId } = params as {
      tenant: string;
      orgId: string;
      supplierId?: string;
    };

    if (supplierId && !isRecordId(supplierId)) {
      throw notFound();
    }

    const activeOrganisation = await resolveOrgScope(tenant, orgId, location);
    if (!activeOrganisation) {
      return;
    }

    // Remember where the user is so `/` can drop them back here next visit
    // instead of the org picker. resolveOrgScope already validated org +
    // tenant, so this scope is known-good.
    rememberLastScope({ tenant, orgId, supplierId });

    // Only need the supplier list to resolve the supplier when the URL doesn't
    // already carry one — skip the fetch (and its await) on canonical
    // supplier-scoped URLs, which is every warm navigation. The bare org URL
    // (`/$tenant/$orgId`) is the supplier picker; `/$tenant/$orgId/supplier` is
    // the deliberate "all suppliers" view, so here we only canonicalise the
    // empty/sole-supplier edge cases.
    if (!supplierId) {
      // Resolve the supplier list for THIS org. The cache key is org-scoped (see
      // getQueryKey), so passing orgId guarantees we read the right org's list —
      // never a stale cross-org entry that would canonicalise onto a foreign
      // supplier.
      const suppliers = await fetchQuery(
        globalSupplierSelectionList,
        SUPPLIER_LIST_VARIABLES,
        orgId,
      )
        .then((data) => data.suppliers.data)
        .catch(() => null);

      // No suppliers yet → non-admins finish setup in the onboarding flow. The
      // org rides in the path so the create-supplier mutation + redirect stay
      // URL-driven. Admin orgs legitimately have no suppliers, so leave them on
      // the unscoped URL rather than pushing them into supplier setup.
      if (suppliers?.length === 0 && !activeOrganisation.is_admin) {
        throw redirect({
          to: "/org/$orgId/create-supplier",
          params: { orgId },
        });
      }
    }
  },
  getParentRoute: () => rootRoute,
  path: "/$tenant/$orgId/supplier/{-$supplierId}",
  loader: ({ params }) => {
    const { orgId, supplierId } = params as {
      orgId: string;
      supplierId?: string;
    };
    const activeSupplierId = supplierParamToId(supplierId);
    // Warm the supplier list, meta, me and the supplier record in parallel.
    //
    // Fire-and-forget: we deliberately DON'T await/return these. Returning the
    // promise turns the loader into a barrier that blocks first paint — and
    // every component-mounted query behind it — until all of them resolve.
    // Instead the layout + page mount immediately and anything that truly needs
    // a value suspends on its own useSuspenseQuery (useGlobalSupplier,
    // DashboardScreen's meta, useUser's me), reusing these in-flight prefetches.
    // The supplier param (absent = all suppliers) is authoritative here.
    prefetchQuery(globalSupplierSelectionList, SUPPLIER_LIST_VARIABLES, orgId);
    prefetchQuery(metaQuery, {}, orgId);
    QueryClient.prefetchQuery(meQueryOptions(useAuthStore.getState().token));
    QueryClient.prefetchQuery({
      queryKey: buildShaQueryKey,
      queryFn: fetchBuildSha,
    });
    if (activeSupplierId) {
      prefetchQuery(supplierRecordQuery, { id: activeSupplierId }, orgId);
    }
  },
  component: AppLayout,
});
